import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Modal, Form, Button, Table } from "react-bootstrap";
import { recalcLinePrice } from "../../../../_utils/manualEstimateItems";
import { gpmFromPrice, priceFromGpm, itemStructureFields } from "../../../../_utils/addScopeItems";
import { PRICING_CONTRACT_LABOR_ITEM_LIST_REQUEST } from "../../../../_utils/constants/PricingContract";
import { LABOR_CATEGORY_REQUEST } from "../../../../_utils/constants/Labor";
import { PRODUCT_TYPE_REQUEST, UNIT_REQUEST } from "../../../../_utils/constants/ProductCatalog";
import { STORE_INFO_REQUEST } from "../../../../_utils/constants/admin/StoreInfo";

// Stable identity for a fetched labor item (used for checkbox / price state keys and de-dupe).
const keyOfLabor = (l) => `${l.laborItemId ?? l.laborId ?? ""}-${l.laborId ?? ""}`;

// Map a GetLaborItemForPricingContract row into an OrderConfirmation-shaped labor row for the scope
// grid (Qty BLANK — keyed in the scope's Labor grid afterward). Mirrors addScopeItems.addonToLabor.
const toScopeLabor = (l, price, gpm) =>
  recalcLinePrice({
    quantity: "",
    unit: String(l.unitName ?? l.unit ?? ""),
    laborCategory: String(l.laborCategory ?? ""),
    labor: String(l.laborName ?? l.labor ?? ""),
    unitPrice: String(price ?? ""),
    unitCost: String(l.currentCost ?? l.unitCost ?? ""),
    gpm: String(gpm ?? ""),
    laborItemId: Number(l.laborItemId ?? l.laborId ?? 0) || 0,
    laborId: Number(l.laborId ?? 0) || 0,
    ...itemStructureFields(l),
    kitId: Number(l.kitId ?? 0) || 0,
    coverage: Number(l.coverage ?? l.Coverage ?? 0) || 0,
    subProductStructureId: Number(l.subProductStructureId ?? l.SubProductStructureId ?? 0) || 0,
    subProductStructure: String(l.subProductStructure ?? l.productStructure ?? ""),
    isPrimary: false,
  });

const AddLaborItemModalBody = ({
  show,
  onClose,
  onAdd,
  structureName,
  laborItemList,
  isLoading,
  storeOptions,
  structureOptions,
  laborCategoryOptions,
  unitOptions,
  filters,
  setFilter,
  excludeLaborIds = null,
}) => {
  // Per-row selection + hand-keyed Price / GPM (kept in sync via the contract formulas).
  const [checked, setChecked] = useState({});
  const [prices, setPrices] = useState({});
  const [gpms, setGpms] = useState({});
  const [priceErrors, setPriceErrors] = useState({});

  // Reset the picker whenever it is reopened so a previous run's picks don't leak in.
  useEffect(() => {
    if (show) {
      setChecked({});
      setPrices({});
      setGpms({});
      setPriceErrors({});
    }
  }, [show]);

  const rows = (Array.isArray(laborItemList) ? laborItemList : []).filter(
    (l) => !excludeLaborIds?.has(Number(l.laborItemId ?? l.laborId ?? 0))
  );

  const setPriceGpm = (k, price, cost) => {
    setPrices((prev) => ({ ...prev, [k]: price }));
    setGpms((prev) => ({ ...prev, [k]: price === "" ? "" : String(gpmFromPrice(price, cost)) }));
  };
  const setGpmPrice = (k, gpm, cost) => {
    setGpms((prev) => ({ ...prev, [k]: gpm }));
    setPrices((prev) => ({ ...prev, [k]: gpm === "" ? "" : String(priceFromGpm(gpm, cost)) }));
  };

  const priceError = (raw) => {
    const s = String(raw ?? "").trim();
    if (s === "") return "Price is required";
    const n = Number(s);
    if (!Number.isFinite(n)) return "Price must be a number";
    if (n < 0) return "Price cannot be negative";
    return "";
  };

  const handleSubmit = () => {
    const picked = rows.filter((l) => checked[keyOfLabor(l)]);
    const errs = {};
    picked.forEach((l) => {
      const k = keyOfLabor(l);
      const err = priceError(prices[k]);
      if (err) errs[k] = err;
    });
    if (Object.keys(errs).length) {
      setPriceErrors(errs);
      return;
    }
    const labor = picked.map((l) => {
      const k = keyOfLabor(l);
      return toScopeLabor(l, prices[k], gpms[k]);
    });
    if (onAdd) onAdd(labor);
  };

  return (
    <Modal show={show} size="xl" dialogClassName="add-scope-modal-dialog" onHide={onClose}>
      <style>{`.add-scope-modal-dialog{max-width:95vw;}`}</style>
      <Modal.Header closeButton>
        <Modal.Title>Add New Labor Item</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <h6 className="fw-bold mb-3" style={{ color: "#2D831F" }}>
          Which labor services would you like to add{structureName ? ` to ${structureName}` : ""}?
        </h6>

        <Table bordered responsive className="text-center">
          <thead>
            <tr>
              <th style={{ width: "50px" }}>Select</th>
              <th style={{ width: "150px" }}>
                Store Name
                <Form.Select
                  size="sm"
                  className="mt-1"
                  aria-label="Filter by store"
                  value={filters.storeId}
                  disabled
                >
                  <option value="">All</option>
                  {storeOptions.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </Form.Select>
              </th>
              <th style={{ width: "160px" }}>
                Product Structure
                <Form.Select
                  size="sm"
                  className="mt-1"
                  aria-label="Filter by product structure"
                  value={filters.productStructureId}
                  onChange={(e) => setFilter("productStructureId", e.target.value)}
                >
                  <option value="">Select</option>
                  {structureOptions.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </Form.Select>
              </th>
              <th style={{ width: "150px" }}>
                Labor Category
                <Form.Select
                  size="sm"
                  className="mt-1"
                  aria-label="Filter by labor category"
                  value={filters.laborCategoryId}
                  onChange={(e) => setFilter("laborCategoryId", e.target.value)}
                >
                  <option value="">Select</option>
                  {laborCategoryOptions.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </Form.Select>
              </th>
              <th>
                Labor Name
                <Form.Control
                  size="sm"
                  className="mt-1"
                  type="text"
                  placeholder="Search"
                  aria-label="Filter by labor name"
                  value={filters.laborName}
                  onChange={(e) => setFilter("laborName", e.target.value)}
                />
              </th>
              <th style={{ width: "90px" }}>
                Unit
                <Form.Select
                  size="sm"
                  className="mt-1"
                  aria-label="Filter by unit"
                  value={filters.unitId}
                  onChange={(e) => setFilter("unitId", e.target.value)}
                >
                  <option value="">Select</option>
                  {unitOptions.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </Form.Select>
              </th>
              <th style={{ width: "100px" }}>Current Cost</th>
              <th style={{ width: "130px" }}>Price</th>
              <th style={{ width: "120px" }}>GPM</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={9} className="text-muted">Loading labor…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={9} className="text-muted">No labor items found.</td></tr>
            ) : (
              rows.map((l) => {
                const k = keyOfLabor(l);
                const cost = l.currentCost ?? l.unitCost;
                return (
                  <tr key={k}>
                    <td>
                      <Form.Check
                        type="checkbox"
                        aria-label={`Select labor ${k}`}
                        checked={!!checked[k]}
                        onChange={(e) => setChecked((prev) => ({ ...prev, [k]: e.target.checked }))}
                      />
                    </td>
                    <td>{l.storeName ?? l.store ?? ""}</td>
                    <td>{l.productType ?? l.productStructure ?? l.productStructureName ?? ""}</td>
                    <td>{l.laborCategory}</td>
                    <td className="text-start">{l.laborName ?? l.labor}</td>
                    <td>{l.unitName ?? l.unit}</td>
                    <td>{cost}</td>
                    <td>
                      <Form.Control
                        type="number"
                        step="0.01"
                        placeholder="Price"
                        aria-label={`Labor ${k} price`}
                        value={prices[k] ?? ""}
                        isInvalid={!!priceErrors[k]}
                        onChange={(e) => setPriceGpm(k, e.target.value, cost)}
                      />
                      {priceErrors[k] ? (
                        <div className="text-danger small mt-1">{priceErrors[k]}</div>
                      ) : null}
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <Form.Control
                          type="number"
                          step="0.01"
                          placeholder="GPM"
                          aria-label={`Labor ${k} GPM`}
                          value={gpms[k] ?? ""}
                          onChange={(e) => setGpmPrice(k, e.target.value, cost)}
                        />
                        <span className="ms-1">%</span>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" type="button" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="success" type="button" onClick={handleSubmit}>
          Submit
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// Connected wrapper — fetches the labor-item list (GetLaborItemForPricingContract) plus the filter
// option lists (stores, product structures, labor categories, units). Labor is listed for the
// scope's store by default; the product-structure filter is NOT pre-applied (show all structures).
const AddLaborItemModal = ({
  show,
  onClose,
  onAdd,
  storeId,
  contactInfoId,
  structureName,
  excludeLaborIds,
  auth: { auth, isAuthenticated },
  pricingContract,
  LaborCategory,
  ProductType,
  unit,
  StoreInfo,
  fetchLaborItems,
  fetchLaborCategory,
  fetchProductType,
  fetchUnit,
  fetchStoreInfo,
}) => {
  const token = isAuthenticated && auth?.[0]?.token;
  // Default the store to the scope's store ("add labor from same store"); no structure pre-filter.
  const [filters, setFilters] = useState({
    storeId: storeId || "",
    productStructureId: "",
    laborCategoryId: "",
    laborName: "",
    unitId: "",
  });
  const setFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

  const storeOptions = useMemo(
    () =>
      (Array.isArray(StoreInfo?.storeInfo) ? StoreInfo.storeInfo : []).map((s) => ({
        label: s.storeName,
        value: s.storeId,
      })),
    [StoreInfo]
  );
  // Only Countertops/Cabinets structures; label from the productType key (falls back to
  // productStructure), value is the structure id used by the labor filter.
  const structureOptions = useMemo(
    () =>
      (Array.isArray(ProductType?.valueList) ? ProductType.valueList : [])
        .filter((p) => p?.isCountertopCabinet === true)
        .map((p) => ({
          label: p.productType ?? p.productStructure,
          value: p.productStructureId,
        })),
    [ProductType]
  );
  const laborCategoryOptions = useMemo(
    () =>
      (Array.isArray(LaborCategory?.laborCategory) ? LaborCategory.laborCategory : []).map((c) => ({
        label: c.laborCategory,
        value: c.laborCategoryId,
      })),
    [LaborCategory]
  );
  const unitOptions = useMemo(
    () =>
      (Array.isArray(unit?.valueList) ? unit.valueList : []).map((u) => ({
        label: u.unitName,
        value: u.unitId,
      })),
    [unit]
  );

  // On open: load any missing filter option lists. (Filters are initialized by useState on mount —
  // the modal unmounts when closed — so no reset here; resetting would create a new `filters`
  // reference and fire the fetch effect a second time.)
  useEffect(() => {
    if (show && token) {
      if (!(LaborCategory?.laborCategory && LaborCategory.laborCategory.length)) fetchLaborCategory(token);
      if (!(ProductType?.valueList && ProductType.valueList.length)) fetchProductType(token);
      if (!(unit?.valueList && unit.valueList.length)) fetchUnit(token);
      if (!(StoreInfo?.storeInfo && StoreInfo.storeInfo.length)) fetchStoreInfo(token);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // (Re)fetch the labor list whenever the modal opens or a filter changes. productStructureId stays
  // 0 unless the user explicitly picks one (no structure pre-filter).
  useEffect(() => {
    if (!show || !token) return;
    const listParamState = {
      productStructureId: filters.productStructureId || 0,
      storeId: filters.storeId || 0,
      laborCategoryId: filters.laborCategoryId || 0,
      laborName: filters.laborName || "",
      unitId: filters.unitId || 0,
      currentCost: "",
      futureCost1: "",
      increaseDate: "",
      ContactInfoId: contactInfoId || "",
      grpPricingContractId: "",
    };
    // checkFlag=true -> /Ordering/api/Order/GetLaborItemForPricingContract (per requirement).
    fetchLaborItems(token, listParamState, true, 0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, contactInfoId, filters]);

  return (
    <AddLaborItemModalBody
      show={show}
      onClose={onClose}
      onAdd={onAdd}
      structureName={structureName}
      laborItemList={pricingContract?.laborItemList}
      isLoading={Boolean(pricingContract?.isLoading)}
      excludeLaborIds={excludeLaborIds}
      storeOptions={storeOptions}
      structureOptions={structureOptions}
      laborCategoryOptions={laborCategoryOptions}
      unitOptions={unitOptions}
      filters={filters}
      setFilter={setFilter}
    />
  );
};

const mapStateToProps = (state) => ({
  auth: state.auth,
  pricingContract: state.pricingContract,
  LaborCategory: state.LaborCategory,
  ProductType: state.ProductType,
  unit: state.unit,
  StoreInfo: state.StoreInfo,
});

const mapDispatchToProps = (dispatch) => ({
  fetchLaborItems: (token, listParamState, checkFlag, unitId, jobNum) =>
    dispatch({ type: PRICING_CONTRACT_LABOR_ITEM_LIST_REQUEST, token, listParamState, checkFlag, unitId, jobNum }),
  fetchLaborCategory: (token) => dispatch({ type: LABOR_CATEGORY_REQUEST, token }),
  fetchProductType: (token) => dispatch({ type: PRODUCT_TYPE_REQUEST, token }),
  fetchUnit: (token) => dispatch({ type: UNIT_REQUEST, token }),
  fetchStoreInfo: (token) => dispatch({ type: STORE_INFO_REQUEST, token }),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddLaborItemModal);
